import { ChangeEvent, FC, useCallback, useRef, useState } from "react";
import { useI18n } from "../../hooks/useTranslations";
import styles from "./FileSelector.module.css";
import Inline from "./Inline";
import Input from "./Input";

interface FileSelectorProps {
  onFileSelected: (file: File) => void;
  isInvalid?: boolean;
  allowMultiSelection?: boolean;
}

const FileSelector: FC<FileSelectorProps> = ({ onFileSelected, isInvalid, allowMultiSelection }: FileSelectorProps) => {
  const i18n = useI18n();

  const [isDragging, setIsDragging] = useState(false);

  const imageInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File>();

  const setFileValues = (file?: File | null) => {
    if (file) {
      setFile(file);
      onFileSelected(file);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFileValues(event.target.files?.[0]);
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      !isDragging && setIsDragging(true);
    },
    [isDragging],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging && setIsDragging(false);
    },
    [isDragging],
  );

  const handleDrop = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    setFileValues(file);
    if (imageInputRef.current) {
      imageInputRef.current.files = e.dataTransfer.files;
    }
  }, []);

  return (
    <label onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      <input type="file" onChange={handleFileChange} style={{ display: "none" }} ref={imageInputRef} />
      <Inline>
        <Input
          className={styles["file-selector"]}
          value={
            isDragging
              ? i18n.t.common.dropIt
              : file?.name && !allowMultiSelection
                ? file.name
                : i18n.t.common.dragAndDropOrUploadAnFile
          }
          isInvalid={isInvalid}
          readOnly
          onClick={() => imageInputRef.current?.click()}
        />
      </Inline>
    </label>
  );
};

export default FileSelector;
